import React, { useMemo, useState } from "react";
import {
  Grid,
  Autocomplete,
  TextField,
  Typography,
  Box,
  IconButton,
  ListSubheader,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const topGroupLabels = {
  standardCake: "Standard Cakes",
  specialtyCake: "Specialty Cakes",
  cheesecake: "Cheesecakes",
  // "patisserie" is split by subcategory below
};

function toNorm(value) {
  return (value ?? "").toString().trim().toLowerCase();
}
function titleCase(s) {
  const str = (s ?? "").toString().trim();
  if (!str) return "";
  return str
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}
function baseName(p) {
  return p?.displayName || p?.name || "";
}
function flavorText(p) {
  // Try common keys you might use for flavor/variant
  return (
    p?.flavor ??
    p?.flavour ??
    p?.variant ??
    p?.subtype ??
    p?.subType ??
    p?.flavorName ??
    ""
  );
}

/**
 * Group key:
 * - Non-patisserie: use product.type (unchanged).
 * - Patisserie: "patisserie::<normalizedSubcategory>"
 */
function groupKeyFor(option) {
  const type = option?.type || "other";
  if (type !== "patisserie") return type;
  const rawSub = option?.subcategory ?? option?.subCategory ?? "other";
  const normSub = toNorm(rawSub) || "other";
  return `patisserie::${normSub}`;
}

/** Human label for a group key */
function groupLabelFor(groupKey) {
  if (groupKey.startsWith("patisserie::")) {
    const sub = groupKey.split("::")[1] || "other";
    return `Patisserie — ${titleCase(sub)}`;
  }
  return topGroupLabels[groupKey] || groupKey;
}

const ProductSelectorSection = ({ form, updateField, products }) => {
  const [expandedGroups, setExpandedGroups] = useState({});
  const [inputValue, setInputValue] = useState("");

  // Attach computed group keys to options and keep original index
  const optionsWithGroups = useMemo(() => {
    return (products || []).map((p, idx) => ({
      ...p,
      __groupKey: groupKeyFor(p),
      __idx: idx,
    }));
  }, [products]);

  // Build groups map for contiguous rendering and counts per group for duplicate detection
  const { allOptions, nameCountsByGroup } = useMemo(() => {
    const groups = new Map(); // groupKey -> array of options
    const nameCounts = new Map(); // groupKey -> Map(baseName -> count)

    // Fill groups + per-group name counts
    for (const opt of optionsWithGroups) {
      const gk = opt.__groupKey || "other";
      if (!groups.has(gk)) groups.set(gk, []);
      groups.get(gk).push(opt);

      const base = toNorm(baseName(opt));
      if (!nameCounts.has(gk)) nameCounts.set(gk, new Map());
      const gm = nameCounts.get(gk);
      gm.set(base, (gm.get(base) || 0) + 1);
    }

    // Desired non-patisserie group order (include only if present)
    const preferredTopOrder = ["standardCake", "specialtyCake", "cheesecake"];

    const presentKeys = Array.from(groups.keys());
    const patisserieKeys = presentKeys.filter((k) =>
      k.startsWith("patisserie::")
    );
    const otherKeys = presentKeys.filter(
      (k) => !k.startsWith("patisserie::") && !preferredTopOrder.includes(k)
    );

    // Sort patisserie subgroups by display label for predictable, contiguous blocks
    patisserieKeys.sort((a, b) =>
      groupLabelFor(a).localeCompare(groupLabelFor(b), undefined, {
        sensitivity: "base",
      })
    );

    // Overall group order: preferred top groups -> patisserie subgroups -> any remaining (alpha)
    const orderedKeys = [
      ...preferredTopOrder.filter((k) => groups.has(k)),
      ...patisserieKeys,
      ...otherKeys.sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: "base" })
      ),
    ];

    // Within each group, keep original server order (stable by original index)
    const flattened = [];
    for (const k of orderedKeys) {
      const arr = groups.get(k) || [];
      arr.sort((a, b) => a.__idx - b.__idx);
      flattened.push(...arr);
    }

    return { allOptions: flattened, nameCountsByGroup: nameCounts };
  }, [optionsWithGroups]);

  // Selected option resolver
  const getSelectedOption = () =>
    (products || []).find((p) => p._id === form.productId) || null;

  // Label logic:
  // - Duplicate detection is scoped to the SAME GROUP (type/subcategory).
  // - Skip redundant suffixes (if base already equals or includes the flavor).
  // - Title-case the flavor when shown.
  const getOptionLabel = (option) => {
    const base = baseName(option);
    const groupKey = option.__groupKey || "other";
    const countsMap = nameCountsByGroup.get(groupKey) || new Map();
    const dupCount = countsMap.get(toNorm(base)) || 0;

    if (dupCount <= 1) return base;

    const flavRaw = flavorText(option);
    const flavNorm = toNorm(flavRaw);
    if (!flavNorm) return base;

    const baseNorm = toNorm(base);

    // If base equals flavor or already contains it (e.g., "Baileys" vs "baileys"), skip suffix.
    if (baseNorm === flavNorm || baseNorm.includes(flavNorm)) return base;

    return `${base} - ${titleCase(flavRaw)}`;
  };

  const groupBy = (option) => option.__groupKey || "other";

  const handleChange = (_, selected) => {
    if (selected) {
      updateField("productId", selected._id);
      updateField("name", baseName(selected));
      updateField("type", selected.category);
    } else {
      updateField("productId", "");
      updateField("type", "");
      updateField("name", "");
    }
  };

  return (
    <Grid item xs={12}>
      <Autocomplete
        fullWidth
        options={allOptions}
        getOptionLabel={getOptionLabel}
        groupBy={groupBy}
        value={getSelectedOption()}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={(_, value) => setInputValue(value)}
        renderInput={(params) => (
          <TextField {...params} label="Select Product" />
        )}
        renderGroup={({ key, group, children }) => {
          const display = groupLabelFor(group);
          const isOpen =
            expandedGroups[group] === undefined
              ? true
              : !!expandedGroups[group];
          return (
            <li key={key}>
              <ListSubheader
                component="div"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  bgcolor: "#f0f0f0",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedGroups((prev) => ({
                    ...prev,
                    [group]: !isOpen,
                  }));
                }}>
                <Typography variant="subtitle2">{display}</Typography>
                <IconButton size="small" edge="end">
                  {isOpen ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </IconButton>
              </ListSubheader>
              {(inputValue || isOpen) && <Box>{children}</Box>}
            </li>
          );
        }}
        isOptionEqualToValue={(option, value) => option._id === value._id}
        ListboxProps={{ style: { maxHeight: 400, overflowY: "auto" } }}
      />
    </Grid>
  );
};

export default ProductSelectorSection;
